import { useRouter } from 'next/router';
import getConfig from 'next/config';

import { FeatureFlags } from 'featureFlags/constants';
import { fetchCurrentUser } from 'auth/services/fetchCurrentUser';
import { initializeSnapchatPixel } from 'tracking/utils/snapchatEvents';
import { login, logout } from 'store/slice/authSlice';
import { logInUser } from 'auth/services/logInUser';
import { logOutUser } from 'auth/services/logOutUser';
import { useAccountModalState } from 'account/hooks/useAccountModal';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { useLocation } from 'shared/hooks/useLocation';
import { updateRegionPreferences } from 'account/services/regionPreferences';

export const useAccount = () => {
  const { redirect } = useStoreSelector((state) => state.accountModal);
  const { callbackFunction } = useAccountModalState();
  const storeDispatch = useStoreDispatch();
  const { selectedCountryCode } = useLocation();
  const router = useRouter();
  const isAtoMitigationEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_ATO_MITIGATION);
  const webEnableGoogleRecaptcha = useFeatureFlag(FeatureFlags.WEB_ENABLE_GOOGLE_RECAPTCHA);

  const logInAccount = async ({
    username,
    password,
    captchaToken = '',
    provider = '',
    forceViceroy = '',
    eventContext = '',
    accountCreation = false,
    clientDedupID = '',
    otp,
  }) => {
    const {
      publicRuntimeConfig: { SNAPCHAT_PIXEL_ID },
    } = getConfig();
    await logInUser({
      username,
      password,
      captchaToken,
      provider,
      forceViceroy,
      isAtoMitigationEnabled,
      webEnableGoogleRecaptcha,
      otp,
    });
    await updateRegionPreferences(selectedCountryCode);
    const user = await fetchCurrentUser();

    initializeSnapchatPixel({ user, SNAPCHAT_PIXEL_ID });

    storeDispatch(login({ user, eventContext, accountCreation, clientDedupID }));
    if (redirect) {
      await router.push(redirect);
    }
    if (callbackFunction && typeof callbackFunction === 'function') {
      callbackFunction();
    }
  };

  const logOutAccount = async () => {
    const {
      publicRuntimeConfig: { SNAPCHAT_PIXEL_ID },
    } = getConfig();
    await logOutUser();

    initializeSnapchatPixel({ user: { email: '' }, SNAPCHAT_PIXEL_ID });

    storeDispatch(logout());
    await router.push('/');
  };

  return {
    logInAccount,
    logOutAccount,
  };
};
